// src/components/modal/tabs/DetailsTabContent.tsx
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaClock } from "react-icons/fa";
import { FaPepperHot } from "react-icons/fa6";
import type { MenuItem, Branch } from "~/types/new_types";
import { getItemDescription } from "~/utils/menuItemHelper";
import CrossSellingSection from "~/components/shared/CrossSelling/CrossSellingSection";
import { useBranch } from "~/contexts/BranchContext";

interface DetailsTabContentProps {
  item: MenuItem;
  theme:
    | Branch["theme"]
    | {
        primaryColor?: string;
        secondaryColor?: string;
        backgroundColor?: string;
        cardColor?: string;
        textColor?: string;
        borderColor?: string;
        borderRadius?: string;
        fontFamily?: string;
      };
  language: string;
}

export const DetailsTabContent: React.FC<DetailsTabContentProps> = ({
  item,
  theme,
  language,
}) => {
  const { t } = useTranslation();

  // Check if there's any content to display
  const hasDescription = !!getItemDescription(item, language);
  const hasPrepTime = !!item.preparation_time;
  const hasSpiceLevel = !!item.spice_level;
  const hasSuggestions = item.suggestions && item.suggestions.length > 0;
  const hasContent = hasDescription || hasPrepTime || hasSpiceLevel || hasSuggestions;

  // Empty state when no content
  if (!hasContent) {
    return (
      <motion.div
        key="details-empty"
        className="flex flex-col items-center justify-center py-12 text-center"
        style={{ color: `${theme?.textColor}60` }}
      >
        <p className="mt-12">{t("menuItem.noDetails")}</p>
      </motion.div>
    );
  }

  return (
    <motion.div key="details" className="space-y-6">
      {hasDescription && (
        <div>
          <p
            className="leading-relaxed"
            style={{ color: `${theme?.textColor}90` }}
          >
            {getItemDescription(item, language)}
          </p>
        </div>
      )}

      {/* Preparation time */}
      {hasPrepTime && (
        <div
          className="flex items-center gap-3 p-4 rounded-lg"
          style={{
            backgroundColor: `${theme?.primaryColor}10`,
            borderRadius: theme?.borderRadius,
          }}
        >
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: `${theme?.primaryColor}20`,
              color: theme?.primaryColor,
            }}
          >
            <FaClock size={20} />
          </div>
          <div>
            <div className="font-medium" style={{ color: theme?.textColor }}>
              {t("menu.preparationTime")}
            </div>
            <div className="text-lg font-bold">
              {item.preparation_time}{" "}
              {item.preparation_time! <= 10
                ? t("menu.minutes")
                : t("menu.minute")}
            </div>
          </div>
        </div>
      )}

      {/* Spice level */}
      {hasSpiceLevel && (
        <div
          className="flex items-center gap-3 p-4 rounded-lg"
          style={{
            backgroundColor: `${theme?.secondaryColor}10`,
            borderRadius: theme?.borderRadius,
          }}
        >
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: `${theme?.secondaryColor}20`,
              color: theme?.secondaryColor,
            }}
          >
            <FaPepperHot />
          </div>
          <div>
            <div className="font-medium" style={{ color: theme?.textColor }}>
              {t("menu.spiceLevel")}
            </div>
            <div
              className="text-lg font-bold capitalize"
              style={{ color: theme?.secondaryColor }}
            >
              {t("spiceLevels." + item.spice_level)}
            </div>
          </div>
        </div>
      )}

      {/* Cross-Selling Section */}
      <CrossSellingSection suggestions={item.suggestions} />
    </motion.div>
  );
};
